import React, { Dispatch, MouseEventHandler, SetStateAction } from 'react';

import {
    Card,
    CardHeader,
    Checkbox,
    Divider,
    IconButton,
    LinearProgress,
    Tooltip,
} from '@material-ui/core';
import { Warning } from '@material-ui/icons';

import { useStyles } from '../../Themable.hooks';

import TranslationHelper from '../../../../helpers/TranslationHelper';

import { ITEMS_TOGGLE_LIMIT } from '../../CommonTransferList';
import CustomListItems from './CustomListItems/CustomListItems';

interface IOwnProps<T> {
    loaded: boolean;
    title: string;
    items: T[];
    handleToggle: (value: T) => MouseEventHandler | undefined;
    checked: T[];
    setChecked: Dispatch<SetStateAction<T[]>>;
    showTypeFilter?: boolean;
    utils: IBaseListUtils<T>;
    renderSecondary?: (item: T) => React.ReactNode;
    uniqueKey: (item: T) => string;
    filterComponents: () => React.ReactNode;
}

export interface IBaseTransferListObject {
    relationId: string;
    name: string;
    type?: string;
}

interface IBaseListUtils<T> {
    intersection: (a: T[], b: T[]) => T[];
    union: (a: T[], b: T[]) => T[];
    not: (a: T[], b: T[]) => T[];
    findIndexById: (arr: T[], item: T) => number;
}

const CustomList = <T extends IBaseTransferListObject>({
    loaded,
    title,
    items,
    handleToggle,
    checked,
    setChecked,
    utils,
    renderSecondary,
    uniqueKey,
    filterComponents,
}: IOwnProps<T>) => {
    const classes = useStyles();
    const { intersection, union, not, findIndexById } = utils;

    const numberOfChecked = (selectedItems: T[]) =>
        intersection(checked, selectedItems).length;

    const handleToggleAll = (items: T[]) => () => {
        if (numberOfChecked(items) === items.length) {
            setChecked(not(checked, items));
        } else {
            setChecked(union(checked, items));
        }
    };

    return (
        <Card className={classes.card}>
            <CardHeader className={classes.cardHeader} title={title} />

            <div className={classes.filtersWrapper}>{filterComponents()}</div>
            <div className={classes.selectAllWrapper}>
                <Checkbox
                    onClick={handleToggleAll(items)}
                    checked={
                        numberOfChecked(items) === items.length &&
                        items.length !== 0
                    }
                    indeterminate={
                        numberOfChecked(items) !== items.length &&
                        numberOfChecked(items) !== 0
                    }
                    disabled={
                        items.length === 0 ||
                        items.length > ITEMS_TOGGLE_LIMIT ||
                        !loaded
                    }
                    inputProps={{ 'aria-label': 'all items selected' }}
                />
                <div>{`${numberOfChecked(items)}/${
                    items.length
                } ${TranslationHelper.translate('Selected')}`}</div>
                {items.length >= ITEMS_TOGGLE_LIMIT && (
                    <Tooltip
                        title={TranslationHelper.translate(
                            'assignToGroup.tooMany'
                        )}
                    >
                        <IconButton size="small">
                            <Warning className={classes.warningIcon} />
                        </IconButton>
                    </Tooltip>
                )}
            </div>
            <Divider />
            {loaded ? (
                <CustomListItems
                    loaded={loaded}
                    items={items}
                    uniqueKey={uniqueKey}
                    handleToggle={handleToggle}
                    findIndexById={findIndexById}
                    checked={checked}
                    renderSecondary={renderSecondary}
                />
            ) : (
                <LinearProgress />
            )}
        </Card>
    );
};

export default CustomList;
